import { FunctionComponent, useState } from 'react'
import { useNavigate } from 'react-router'
import { Alert, Button, Form, Spinner } from 'react-bootstrap'
import { Link } from 'react-router-dom'

import AuthSplitLayout from 'components/layout/auth/authSplitLayout'
import PasswordInput from 'components/forms/passwordInput'
import authLinksCss from 'components/layout/auth/authLinks.module.scss'

import { isValidEmail } from 'utilities/email.util'
import * as userApi from '../api.client'
import { NewUser } from './types'

const passwordMinLength = 8

const NewUserInitialState = {
  name: '',
  email: '',
  password: '',
  passwordConfirmation: '',
}

const SignUp: FunctionComponent = () => {
  const navigate = useNavigate()
  const [newUser, setNewUser] = useState<NewUser>(NewUserInitialState)
  const [nameSet, setNameSet] = useState(false)
  const [emailSet, setEmailSet] = useState(false)
  const [passwordSet, setPasswordSet] = useState(false)
  const [passwordConfirmationSet, setPasswordConfirmationSet] = useState(false)
  const [validEmail, setValidEmail] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [passwordsMatch, setPasswordsMatch] = useState(false)
  const [passwordHasMinLength, setPasswordHasMinLength] = useState(false)
  const [signingUp, setSigningUp] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string>()

  const signUp = async () => {
    if (!validateUserData()) {
      return
    }

    try {
      setSigningUp(true)
      await userApi.register(newUser)
      navigate('/login')
    } catch (error: any) {
      setSigningUp(false)
      setErrorMessage(error.messages.join(', '))
    }
  }

  const handleFormChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target

    if (newUser) {
      setNewUser({ ...newUser, [name]: value })
    }
  }

  const validateEmail = () => {
    if (newUser) {
      const emailSet = newUser.email.trim().length > 0
      setEmailSet(emailSet)
      setValidEmail(isValidEmail(newUser.email))
    }
  }

  const validateUserData = (): boolean => {
    const nameSet = newUser.name.trim().length > 0
    const emailSet = newUser.email.trim().length > 0
    const passwordSet = newUser.password.trim().length > 0
    const passwordConfirmationSet =
      newUser.passwordConfirmation.trim().length > 0
    const passwordsMatch =
      passwordSet &&
      passwordConfirmationSet &&
      newUser.password === newUser.passwordConfirmation
    const passwordHasMinLength =
      newUser.password.trim().length >= passwordMinLength

    setSubmitted(true)
    setNameSet(nameSet)
    setPasswordSet(passwordSet)
    setPasswordConfirmationSet(passwordConfirmationSet)
    setPasswordsMatch(passwordsMatch)
    setPasswordHasMinLength(passwordHasMinLength)

    return (
      nameSet &&
      emailSet &&
      validEmail &&
      passwordSet &&
      passwordConfirmationSet &&
      passwordsMatch &&
      passwordHasMinLength
    )
  }

  const fieldError = (message: string) => (
    <span className="invalid-feedback d-block">{message}</span>
  )

  return (
    <AuthSplitLayout>
      <h1 className="page-title">Crie uma nova conta</h1>
      <p className="page-subtitle">Bem-vindo ao Clic-repasse.</p>

      <div className="mt-4">
        {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
      </div>

      <Form
        noValidate
        onSubmit={(event) => {
          event.preventDefault()
          signUp()
        }}
      >
        <Form.Group className="mb-3" controlId="signup-name">
          <Form.Label>Nome</Form.Label>
          <Form.Control
            type="text"
            name="name"
            autoComplete="name"
            isInvalid={submitted && !nameSet}
            onChange={handleFormChange}
          />
          {submitted && !nameSet && fieldError('Informe seu nome')}
        </Form.Group>

        <Form.Group className="mb-3" controlId="signup-email">
          <Form.Label>Email</Form.Label>
          <Form.Control
            type="email"
            name="email"
            autoComplete="email"
            isInvalid={(submitted && !emailSet) || (!validEmail && emailSet)}
            onChange={handleFormChange}
            onBlur={validateEmail}
          />
          {submitted && !emailSet && fieldError('Informe seu email')}
          {!validEmail && emailSet && fieldError('Informe um email válido')}
        </Form.Group>

        <Form.Group className="mb-3" controlId="signup-password">
          <Form.Label>Senha</Form.Label>
          <PasswordInput
            name="password"
            autoComplete="new-password"
            minLength={passwordMinLength}
            isInvalid={submitted && (!passwordSet || !passwordHasMinLength)}
            onChange={handleFormChange}
          />
          {submitted && !passwordSet
            ? fieldError('Crie uma senha com no mínimo 8 caracteres')
            : submitted && !passwordHasMinLength
            ? fieldError('A senha deve conter no mínimo 8 caracteres')
            : <Form.Text>Mínimo de 8 caracteres.</Form.Text>}
        </Form.Group>

        <Form.Group controlId="signup-password-confirmation">
          <Form.Label>Confirmar senha</Form.Label>
          <PasswordInput
            name="passwordConfirmation"
            autoComplete="new-password"
            minLength={passwordMinLength}
            isInvalid={
              submitted &&
              (!passwordConfirmationSet || (passwordSet && !passwordsMatch))
            }
            onChange={handleFormChange}
          />
          {submitted && !passwordConfirmationSet && fieldError('Confirme a senha')}
          {submitted &&
            passwordSet &&
            passwordConfirmationSet &&
            !passwordsMatch &&
            fieldError('As senhas não conferem')}
        </Form.Group>

        <Button type="submit" size="lg" className="w-100 mt-4" disabled={signingUp}>
          {signingUp && <Spinner size="sm" animation="border" />}
          {signingUp ? 'Criando conta…' : 'Criar conta'}
        </Button>

        <p className={`${authLinksCss.links} ${authLinksCss.center}`}>
          <span>
            Já tem conta?{' '}
            <Link to="/login" className={authLinksCss.strong}>
              Entrar
            </Link>
          </span>
        </p>
      </Form>
    </AuthSplitLayout>
  )
}

export default SignUp
